import React, { useContext, useRef, useEffect } from 'react'
import { Input, Typography, Grid } from 'antd'
import { useTranslation } from 'react-i18next'
import updateSession from '../../utils/session/updateSession'
import { FileTextOutlined } from '@ant-design/icons'
import RecordingContext from './RecordingContext'

const { Text } = Typography
const { useBreakpoint } = Grid

export default function RecordingNotesSection({ sessionUuid }) {
  const { t } = useTranslation()
  const screens = useBreakpoint()
  const { userNotes, setUserNotes } = useContext(RecordingContext)
  const [isEditing, setIsEditing] = React.useState(false)
  const [isContainerFocused, setIsContainerFocused] = React.useState(false)
  const [hasChanges, setHasChanges] = React.useState(false)
  const mainInputRef = useRef(null)

  // Handle sessionUuid changes
  useEffect(() => {
    if (!sessionUuid) {
      // Reset state when session is closed
      setUserNotes('')
      setIsEditing(false)
      setIsContainerFocused(false)
      setHasChanges(false)
    } else if (userNotes) {
      // Save notes when session becomes available
      updateUserNotes(userNotes)
    }
  }, [sessionUuid])

  const updateUserNotes = async (noteContent) => {
    if (!sessionUuid) return // Don't save if there's no session yet
    try {
      await updateSession(sessionUuid, { user_notes: noteContent });
      setHasChanges(false)
    } catch (err) {
      console.error('Failed to update session notes:', err)
    }
  }

  const resetZoomOnMobile = () => {
    if (!screens.md) {
      document.activeElement?.blur()
      window.scrollTo(0, 0)
    }
  }

  const handleNoteChange = (e) => {
    const newNote = e.target.value
    setUserNotes(newNote)
    setHasChanges(true)
  }

  const handleExitEditMode = () => {
    if (hasChanges) {
      updateUserNotes(userNotes)
    }
    setIsEditing(false)
  }

  const handleEnterEditMode = () => {
    setIsEditing(true)
    // Use setTimeout to ensure the textarea is rendered before focusing
    setTimeout(() => {
      mainInputRef.current?.focus()
    }, 0)
  }

  const formatNoteForDisplay = (noteText) => {
    if (!noteText) return (
      <Text type="secondary" style={{fontSize: 15, color: '#999'}} data-testid="recording-notes-placeholder">
        {t('recording_notes_section.placeholder')}
      </Text>
    )
    return (
      <div style={{ 
        maxHeight: '240px', // 10 rows * 24px line height
        overflow: 'auto'
      }}>
        {noteText.split('\n').map((line, index) => (
          <div 
            key={index}
            style={{
              padding: '4px 0',
              lineHeight: '1.6',
              color: '#333',
              fontSize: 15,
            }}
          >
            {line}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{marginTop: 35, textAlign: 'left', width: '100%'}}>
      <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: 12}}>
        <FileTextOutlined style={{marginRight: 8, marginTop: 4}}/>
        <div style={{display: 'flex', flexDirection: 'column', flex: 1}}>
          <Text>{t('recording_notes_section.title')}</Text>
        </div>
      </div>
      <div
        style={{
          border: isEditing 
            ? (isContainerFocused ? '1px solid #008B8B' : '1px solid #d9d9d9')
            : '1px solid #d9d9d9',
          borderRadius: 8,
          padding: 10,
          minHeight: 30,
          marginTop: 5,
          marginBottom: 10,
          textAlign: 'left',
          position: 'relative',
          backgroundColor: isEditing ? 'white' : '#fcfcfc',
        }}
      >
        {isEditing ? (
          <Input.TextArea
            placeholder={!isContainerFocused && !userNotes ? t('recording_notes_section.placeholder') : ''}
            autoSize={{ minRows: 2, maxRows: 10 }}
            value={userNotes}
            onChange={handleNoteChange}
            ref={mainInputRef}
            onFocus={() => setIsContainerFocused(true)}
            onBlur={() => {
              setIsContainerFocused(false)
              resetZoomOnMobile()
              handleExitEditMode()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleExitEditMode()
              }
            }}
            style={{
              fontSize: 16,
              border: 'none',
              boxShadow: 'none',
              resize: 'none',
              padding: 0,
              lineHeight: '1.6',
            }}
          />
        ) : (
          <div 
            style={{ 
              cursor: 'text',
              backgroundColor: '#fcfcfc',
              minHeight: '48px', // 2 rows * 24px line height
            }}
            onClick={handleEnterEditMode}
          >
            {formatNoteForDisplay(userNotes)}
          </div>
        )}
      </div>
    </div>
  )
}